import React, { useState } from 'react';
import { Edit2, Trash2, GripVertical, AlertCircle, Check } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { supabase } from '../../lib/supabase';

// Import all available icons for rendering
import * as LucideIcons from 'lucide-react';

interface LeadStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

interface LeadStatusListProps {
  statuses: LeadStatus[];
  onEdit: (status: LeadStatus) => void;
  onReorder: () => void;
  onDelete: (id: string) => void;
}

export function LeadStatusList({ statuses, onEdit, onReorder, onDelete }: LeadStatusListProps) {
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(null);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update the display_order property
    const reorderedItems = items.map((item, index) => ({
      ...item,
      display_order: index
    }));

    try {
      setReordering(true);
      setError(null);

      // Save the new order to the database
      const { data, error } = await supabase.rpc('reorder_lead_statuses', {
        p_status_ids: reorderedItems.map(item => item.id)
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      setSuccess('Status order updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      // Notify parent component to refresh the list
      onReorder();
    } catch (error: any) {
      console.error('Error reordering statuses:', error);
      setError(error.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setReordering(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmation(id);
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      setReordering(true);
      setError(null);

      // Delete the status
      const { data, error } = await supabase.rpc('delete_lead_status', {
        p_id: id
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      setSuccess('Status deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      // Notify parent component to refresh the list
      onDelete(id);
    } catch (error: any) {
      console.error('Error deleting status:', error);
      setError(error.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setReordering(false);
      setDeleteConfirmation(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  // Render status badges with the correct icon and color
  const renderStatusBadge = (status: LeadStatus) => {
    const IconComponent = LucideIcons[status.icon as keyof typeof LucideIcons] as React.FC<{ className?: string }>;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}>
        {IconComponent && <IconComponent className="h-3 w-3 mr-1" />}
        {status.name}
      </span>
    );
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {error && (
        <div className="p-4 bg-red-50">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="lead-statuses">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="divide-y divide-gray-200"
            >
              {statuses.map((status, index) => (
                <Draggable key={status.id} draggableId={status.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div {...provided.dragHandleProps} className="mr-3 cursor-move">
                            <GripVertical className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center">
                              {renderStatusBadge(status)}
                              {status.is_default && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Default
                                </span>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Slug: {status.slug}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onEdit(status)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                          {deleteConfirmation === status.id ? (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleConfirmDelete(status.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={handleCancelDelete}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleDeleteClick(status.id)}
                              className="text-red-600 hover:text-red-900"
                              disabled={status.is_default}
                              title={status.is_default ? "Cannot delete default status" : "Delete status"}
                            >
                              <Trash2 className={`h-5 w-5 ${status.is_default ? 'opacity-50 cursor-not-allowed' : ''}`} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}